// Intake answers (docs/contract.md §1, §2). Every field is optional while drafting;
// validateForSubmit (validate.ts) decides what submit requires.
import { z } from 'zod';
import { FUNCTION_IDS } from './catalog.js';
/** Option value → display label, in display order (except docsYears: see DOCS_YEARS_ORDER). */
export const OPTIONS = {
    firmSize: { just_me: 'Just me', '2_5': '2 to 5', '6_10': '6 to 10', '11_25': '11 to 25', '26_plus': '26 or more' },
    services: {
        individual_tax: 'Individual tax (1040)', business_tax: 'Business tax (1065, 1120, 1120-S)',
        trusts_estates: 'Trusts and estates (1041)', nonprofits: 'Nonprofits (990)', bookkeeping: 'Bookkeeping',
        client_payroll: 'Client payroll', tax_planning: 'Tax planning', advisory_cfo: 'Advisory or fractional CFO',
        sales_tax: 'Sales tax', audit_assurance: 'Audit and assurance',
    },
    targetTiming: { asap: 'As soon as possible', before_tax_season: 'Before tax season', after_tax_season: 'After tax season', flexible: "We're flexible" },
    groups: { in_software: 'Yes, in my software', informal: 'Yes, but only informally', no: 'No' },
    templates: { in_software: 'Yes, in my software', in_documents: 'Yes, in spreadsheets or documents', none: 'No, it is in my head' },
    clientList: { clean: 'Clean and current', some_duplicates: 'Some duplicates or old clients', needs_cleanup: 'Needs a cleanup' },
    timeBilling: { yes_need_history: 'Yes, and we need our time history', yes_history_stays: 'Yes, but history can stay behind', no: 'No' },
    docsScope: {
        filed_returns: 'Filed tax returns', engagement_letters: 'Signed engagement letters',
        client_docs_current_year: 'Client documents for this tax year', organizers: 'Organizers and questionnaires',
        none: "None, we'll start fresh",
    },
    docsYears: { current: 'Current year only', '2': 'Last 2 years', '3': 'Last 3 years', '5_plus': 'Last 5 years or more' },
    docsSize: { under_5gb: 'Under 5 GB', '5_25gb': '5 to 25 GB', over_25gb: 'More than 25 GB', not_sure: 'Not sure' },
    interests: {
        how_move_works: 'How the move works', timeline: 'Timeline and freeze date', client_data: 'Moving our client data',
        portal_esign: 'Client portal and e-signature', billing: 'Billing and payments', tax_software: 'Working with our tax software',
        training: 'Training our team', pricing: 'Pricing',
    },
};
/** Integer-like keys ('2', '3') always enumerate first, so docsYears needs its order spelled out. */
export const DOCS_YEARS_ORDER = ['current', '2', '3', '5_plus'];
export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const keys = (o) => Object.keys(o);
const text = (max = 500) => z.string().trim().max(max);
const count = z.number().int().min(0).max(1_000_000);
/** A calendar date, YYYY-MM-DD (no time or zone). */
const isoDate = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, 'Use a date like 2027-05-01.');
export const ContactSchema = z.object({
    name: text(200),
    role: text(200).nullable(),
    email: text(320),
    phone: text(50),
}).partial();
export const BreakdownSchema = z.object({
    individual: count.nullable(),
    business: count.nullable(),
    trust: count.nullable(),
    nonprofit: count.nullable(),
}).partial();
export const IntakeAnswersSchema = z.object({
    // Step 1
    firmName: text(200),
    website: text(300).nullable(),
    contact: ContactSchema,
    statesText: text(500),
    logins: z.number().int().min(1).max(10_000),
    firmSize: z.enum(keys(OPTIONS.firmSize)),
    services: z.array(z.enum(keys(OPTIONS.services))).max(10),
    targetTiming: z.enum(keys(OPTIONS.targetTiming)),
    targetDate: isoDate.nullable(),
    // Step 2 closing questions
    integrationsText: text(4000).nullable(),
    fallbackText: text(4000).nullable(),
    // Step 3
    clientsTotal: count,
    breakdown: BreakdownSchema,
    openJobs: count,
    groups: z.enum(keys(OPTIONS.groups)),
    templates: z.enum(keys(OPTIONS.templates)),
    clientList: z.enum(keys(OPTIONS.clientList)),
    timeBilling: z.enum(keys(OPTIONS.timeBilling)),
    renewalMonth: z.union([z.number().int().min(1).max(12), z.literal('varies')]),
    // Step 4
    docsScope: z.array(z.enum(keys(OPTIONS.docsScope))).max(5),
    docsYears: z.enum(keys(OPTIONS.docsYears)),
    docsSize: z.enum(keys(OPTIONS.docsSize)),
    docsOtherLocation: text(1000).nullable(),
    // Step 5 (all optional): what they want from the Discovery Session
    interests: z.array(z.enum(keys(OPTIONS.interests))).max(8),
    questionsText: text(4000).nullable(),
    beforeMeetingText: text(4000).nullable(),
}).partial().strict();
/**
 * Autosave body (PATCH /api/intake/steps/:step). Same fields as IntakeAnswersSchema, but every
 * top-level field and every contact/breakdown sub-field also accepts null, which clears the saved answer.
 */
const ContactPatchSchema = z.object({
    name: text(200).nullable(),
    role: text(200).nullable(),
    email: text(320).nullable(),
    phone: text(50).nullable(),
}).partial().strict();
const clearable = (shape) => Object.fromEntries(Object.entries(shape).map(([k, v]) => [k, z.nullable(v).optional()]));
export const IntakeAnswersPatchSchema = z.object({
    ...clearable(IntakeAnswersSchema.shape),
    contact: ContactPatchSchema.nullable().optional(),
    breakdown: BreakdownSchema.strict().nullable().optional(),
}).strict();
/** The Review and submit step: the last questionnaire step. 7 is booking, 8 the confirmation. */
export const REVIEW_STEP = 6;
/** Which answer keys each step owns (used for PATCH /api/intake/steps/:step). */
export const STEP_FIELDS = {
    1: ['firmName', 'website', 'contact', 'statesText', 'logins', 'firmSize', 'services', 'targetTiming', 'targetDate'],
    2: ['integrationsText', 'fallbackText'],
    3: ['clientsTotal', 'breakdown', 'openJobs', 'groups', 'templates', 'clientList', 'timeBilling', 'renewalMonth'],
    4: ['docsScope', 'docsYears', 'docsSize', 'docsOtherLocation'],
    5: ['interests', 'questionsText', 'beforeMeetingText'],
};
export const ToolRowSchema = z.object({
    tools: z.array(z.string().regex(/^[a-z0-9_]{1,64}$/)).max(40),
    other: text(200).nullable(),
    notApplicable: z.boolean(),
    usageNote: text(2000).nullable(),
});
export const emptyToolRow = () => ({ tools: [], other: null, notApplicable: false, usageNote: null });
export const emptyToolRows = () => Object.fromEntries(FUNCTION_IDS.map((id) => [id, emptyToolRow()]));
/**
 * Applies the exclusivity rules the server enforces without erroring:
 * N/A clears tools and Other; "none" in docsScope clears the rest.
 */
export function normalizeToolRow(row) {
    // '' = Other selected but not typed yet (a submit error); null = Other not selected.
    const other = row.other === null ? null : row.other.trim();
    if (row.notApplicable)
        return { tools: [], other: null, notApplicable: true, usageNote: row.usageNote };
    return { tools: [...new Set(row.tools)], other, notApplicable: false, usageNote: row.usageNote?.trim() || null };
}
export function normalizeAnswers(a) {
    const out = { ...a };
    if (out.docsScope?.includes('none'))
        out.docsScope = ['none'];
    if (out.services)
        out.services = [...new Set(out.services)];
    if (out.interests)
        out.interests = [...new Set(out.interests)];
    return out;
}
export const isUnanswered = (row) => !row || (!row.notApplicable && row.tools.length === 0 && row.other === null);
export const rowHas = (row, tool) => !!row && row.tools.includes(tool);
export const anyRowHas = (rows, fns, tool) => fns.some((f) => rowHas(rows[f], tool));
export const optionLabel = (key, value) => value == null ? null : (OPTIONS[key][value] ?? value);
export const renewalLabel = (m) => m == null ? null : m === 'varies' ? 'It varies' : MONTHS[m - 1] ?? null;
//# sourceMappingURL=answers.js.map